import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import type { RankEntry } from '../types';
import { useScenarioStore } from '../store/useScenarioStore';
import { useAppModeStore } from '../store/useAppModeStore';
import TCOSummaryTable from '../components/compare/TCOSummaryTable';
import RankingPanel from '../components/compare/RankingPanel';
import DecisionSummaryPanel from '../components/compare/DecisionSummaryPanel';
import DepreciationChart from '../components/compare/DepreciationChart';
import YearByCostChart, { GroupedYearByCostChart, deriveYearsFromVehicleResult } from '../components/compare/YearByCostChart';
import { VEHICLE_COLORS } from '../components/compare/TCOSummaryTable';
import TCOBreakdownChart from '../components/compare/TCOBreakdownChart';
import BreakEvenPanel from '../components/compare/BreakEvenPanel';
import SensitivityHeatmap from '../components/compare/SensitivityHeatmap';
import EmotionalOverlayPanel from '../components/compare/EmotionalOverlayPanel';
import ImportExportBar from '../components/ImportExportBar';
import ExportCsvButton from '../components/ExportCsvButton';
import ExportPdfButton from '../components/ExportPdfButton';
import { AccentCard, Eyebrow } from '../components/design';
import { FlatCostModelNote } from './ScenarioComparePage';

function SectionHeading({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <h2 style={{
        margin: 0,
        fontSize: 17,
        fontWeight: 600,
        letterSpacing: '-0.01em',
        color: 'var(--tco-ink)',
      }}>
        {title}
      </h2>
      {subtitle && (
        <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--tco-ink-2)' }}>
          {subtitle}
        </p>
      )}
    </div>
  );
}

export default function ComparePage() {
  const navigate = useNavigate();
  const { computeResults, currentScenario, currentScenarioId } = useScenarioStore();
  // Phase 24: nav-level mode is the master gate for cross-cutting surfaces.
  const isIndividual = useAppModeStore((s) => s.isIndividual);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const [liveRankingState, setLiveRankingState] = useState<{
    combinedRanking: RankEntry[];
    financialWeight: number;
    emotionalWeight: number;
  } | null>(null);

  if (!computeResults) {
    return (
      <div style={{ maxWidth: 640, margin: '0 auto', padding: '96px 24px', textAlign: 'center' }}>
        <div style={{
          width: 64, height: 64, margin: '0 auto 20px', display: 'grid', placeItems: 'center',
          borderRadius: '50%', background: 'var(--tco-line-soft)',
        }}>
          <svg width="28" height="28" fill="none" viewBox="0 0 24 24" stroke="var(--tco-ink-3)" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        </div>
        <h1 style={{ fontSize: 22, fontWeight: 600, color: 'var(--tco-ink)', marginBottom: 8 }}>
          No analysis results yet
        </h1>
        <p style={{ fontSize: 14, color: 'var(--tco-ink-2)', marginBottom: 24 }}>
          Run an analysis from the scenario editor to see your vehicle comparison dashboard.
        </p>
        <button
          onClick={() => navigate('/')}
          style={{
            background: 'var(--tco-ink)',
            color: 'var(--tco-bg)',
            border: 'none',
            borderRadius: 6,
            padding: '10px 16px',
            fontSize: 14,
            fontWeight: 550,
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          Go to Scenarios
        </button>
      </div>
    );
  }

  const {
    vehicles,
    financial_ranking,
    emotional_ranking,
    combined_ranking,
    decision_confidence,
    sensitivity,
  } = computeResults;

  const displayedCombinedRanking = liveRankingState?.combinedRanking ?? combined_ranking;
  const displayedFinancialWeight = liveRankingState?.financialWeight ?? currentScenario.financial_weight ?? 0.70;
  const displayedEmotionalWeight = liveRankingState?.emotionalWeight ?? currentScenario.emotional_weight ?? 0.30;

  const scenarioHorizonMonths = currentScenario.horizon_months ?? 60;
  const horizonMonths = vehicles.map(() => scenarioHorizonMonths);

  return (
    <div style={{
      maxWidth: 1140,
      margin: '0 auto',
      padding: '32px 32px 48px',
      display: 'flex',
      flexDirection: 'column',
      gap: 28,
    }}>
      {/* Page header */}
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between' }}>
        <div>
          <Eyebrow style={{ marginBottom: 6 }}>Compare</Eyebrow>
          <h1 style={{
            margin: 0,
            fontSize: 28,
            fontWeight: 600,
            letterSpacing: '-0.015em',
            color: 'var(--tco-ink)',
          }}>
            {currentScenario.name}
          </h1>
          <p style={{ margin: '6px 0 0', fontSize: 13, color: 'var(--tco-ink-2)' }}>
            {scenarioHorizonMonths / 12}-yr horizon · {(currentScenario.annual_miles ?? 0).toLocaleString('en-US')} mi/yr
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <ExportCsvButton
            kind="scenario"
            scenario={currentScenario}
            mode="personal"
            personalResult={computeResults}
          />
          <ExportPdfButton
            kind="scenario"
            scenario={currentScenario}
            mode="personal"
            personalResult={computeResults}
          />
          <button
            onClick={() => navigate(-1)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              background: 'transparent',
              border: 'none',
              color: 'var(--tco-ink-2)',
              fontSize: 13,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>
        </div>
      </div>

      {/* Import / Export toolbar */}
      <ImportExportBar
        scenarioId={currentScenarioId}
        currentScenario={currentScenario}
      />

      {/* Decision Summary — Personal mode only.
          Phase 24: emotional weighting and "what should I buy" framing don't
          apply to fleet operators, who are optimising on cost-per-mile. */}
      {isIndividual && (
        <section>
          <SectionHeading
            title="Decision summary"
            subtitle="Top recommendation based on your financial and emotional weights"
          />
          <DecisionSummaryPanel
            financialRanking={financial_ranking}
            emotionalRanking={emotional_ranking}
            combinedRanking={displayedCombinedRanking}
            decisionConfidence={decision_confidence}
            financialWeight={displayedFinancialWeight}
            emotionalWeight={displayedEmotionalWeight}
          />
        </section>
      )}

      {/* Rankings */}
      <section>
        <SectionHeading
          title="Rankings"
          subtitle="Vehicles ranked by financial cost, emotional value, and combined score"
        />
        <RankingPanel
          financialRanking={financial_ranking}
          emotionalRanking={emotional_ranking}
          combinedRanking={combined_ranking}
          decisionConfidence={decision_confidence}
          financialWeight={currentScenario.financial_weight ?? 0.70}
          emotionalWeight={currentScenario.emotional_weight ?? 0.30}
          onLiveRankingChange={(ranking, fw, ew) =>
            setLiveRankingState({ combinedRanking: ranking, financialWeight: fw, emotionalWeight: ew })
          }
        />
      </section>

      {/* TCO Summary Table */}
      <section data-tour="tco-summary">
        <SectionHeading
          title="TCO summary"
          subtitle="Side-by-side cost breakdown — green = best, red = worst"
        />
        <TCOSummaryTable
          vehicles={vehicles}
          horizonMonths={horizonMonths}
          purchasePrices={vehicles.map(vr => {
            const vi = currentScenario.vehicles.find(v => v.vehicle_id === vr.vehicle_id);
            return vi?.depreciation.purchase_price ?? 0;
          })}
        />
      </section>

      {/* Phase 24.3: Year-by-year cost walk (personal single-scenario) */}
      <section>
        <SectionHeading
          title="Year-by-year cost walk"
          subtitle="Stacked annual costs; toggle to overlay cumulative TCO and a sensitivity envelope"
        />
        <AccentCard padding="20px">
          <PersonalYearByCostBlock
            vehicles={vehicles}
            horizonMonths={scenarioHorizonMonths}
            purchasePrices={vehicles.map((vr) => {
              const vi = currentScenario.vehicles.find((v) => v.vehicle_id === vr.vehicle_id);
              return vi?.depreciation.purchase_price ?? 0;
            })}
          />
          <FlatCostModelNote />
        </AccentCard>
      </section>

      {/* Depreciation Chart */}
      <section data-tour="dep-curve">
        <SectionHeading
          title="Vehicle value over time"
          subtitle="Monthly depreciation path across the ownership horizon"
        />
        <AccentCard padding="20px">
          <DepreciationChart
            vehicles={vehicles}
            purchasePrices={vehicles.map((vr) => {
              const vi = currentScenario.vehicles.find((v) => v.vehicle_id === vr.vehicle_id);
              return vi?.depreciation.purchase_price ?? null;
            })}
          />
        </AccentCard>
      </section>

      {/* TCO Cost Composition — Phase 25.1: 100% proportional. */}
      <section>
        <SectionHeading
          title="TCO Cost Composition"
          subtitle="Cost share by category — hover for dollar amounts"
        />
        <AccentCard padding="20px">
          <TCOBreakdownChart vehicles={vehicles} />
        </AccentCard>
      </section>

      {/* Break-even */}
      <section>
        <SectionHeading
          title="Break-even analysis"
          subtitle="What resale or purchase price would make each non-winner competitive"
        />
        <BreakEvenPanel vehicles={vehicles} financialRanking={financial_ranking} />
      </section>

      {/* Sensitivity */}
      <section>
        <SectionHeading
          title="Sensitivity heatmap"
          subtitle="TCO across APR delta (rows) and residual value multiplier (columns)"
        />
        <SensitivityHeatmap vehicles={vehicles} sensitivity={sensitivity} />
      </section>

      {/* Emotional overlay — Personal mode only. */}
      {isIndividual && (
        <section>
          <SectionHeading
            title="Emotional overlay"
            subtitle="Subjective enjoyment scores, radar profile, and combined-ranking weights"
          />
          <EmotionalOverlayPanel
            vehicles={vehicles}
            vehicleInputs={currentScenario.vehicles}
            financialWeight={currentScenario.financial_weight ?? 0.70}
            emotionalWeight={currentScenario.emotional_weight ?? 0.30}
          />
        </section>
      )}
    </div>
  );
}

// Phase 24.3.2: per-vehicle cost-walk wrapper for personal mode. The compare
// view supports 1-4 vehicles; layout switches based on count:
//   1 vehicle  : single YearByCostChart, no picker
//   2 vehicles : grouped bar chart on shared Y axis (the common Accord-vs-BMW shape)
//   3-4        : dropdown picker into a single YearByCostChart (a grouped chart
//                with 3-4 stacks per year crowds the canvas; keep the picker)
function PersonalYearByCostBlock({
  vehicles,
  horizonMonths,
  purchasePrices,
}: {
  vehicles: import('../types').VehicleResult[];
  horizonMonths: number;
  purchasePrices: number[];
}) {
  const [selectedIdx, setSelectedIdx] = useState(0);
  if (vehicles.length === 0) return null;

  if (vehicles.length === 2) {
    const a = vehicles[0];
    const b = vehicles[1];
    const aYears = deriveYearsFromVehicleResult(a, horizonMonths, purchasePrices[0] ?? 0);
    const bYears = deriveYearsFromVehicleResult(b, horizonMonths, purchasePrices[1] ?? 0);
    const fuelTotal = (v: import('../types').VehicleResult) => {
      const op = v.operating as Record<string, unknown> | null;
      const bd = op?.['operating_breakdown'] as Record<string, unknown> | undefined;
      return Number(bd?.['fuel_cost_total'] ?? 0);
    };
    const depTotal = (v: import('../types').VehicleResult) =>
      Number((v.depreciation as Record<string, unknown>)['total_depreciation'] ?? 0);
    return (
      <GroupedYearByCostChart
        seriesA={{
          name: a.label,
          color: VEHICLE_COLORS[0],
          years: aYears,
          totalFuel: fuelTotal(a),
          totalDepreciation: depTotal(a),
        }}
        seriesB={{
          name: b.label,
          color: VEHICLE_COLORS[1],
          years: bYears,
          totalFuel: fuelTotal(b),
          totalDepreciation: depTotal(b),
        }}
      />
    );
  }

  const idx = Math.min(selectedIdx, vehicles.length - 1);
  const vehicle = vehicles[idx];
  const years = deriveYearsFromVehicleResult(vehicle, horizonMonths, purchasePrices[idx] ?? 0);
  const op = vehicle.operating as Record<string, unknown> | null;
  const bd = op?.['operating_breakdown'] as Record<string, unknown> | undefined;
  const totalFuel = Number(bd?.['fuel_cost_total'] ?? 0);
  const totalDepreciation = Number(
    (vehicle.depreciation as Record<string, unknown>)['total_depreciation'] ?? 0,
  );

  return (
    <div>
      {vehicles.length > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <label htmlFor="ybc-vehicle" style={{ fontSize: 12, color: 'var(--white-dim)' }}>
            Vehicle:
          </label>
          <select
            id="ybc-vehicle"
            value={idx}
            onChange={(e) => setSelectedIdx(Number(e.target.value))}
            style={{
              background: 'transparent',
              color: 'var(--tco-ink)',
              border: '1px solid var(--tco-line)',
              borderRadius: 6,
              padding: '4px 10px',
              fontSize: 13,
              fontFamily: 'inherit',
              cursor: 'pointer',
            }}
          >
            {vehicles.map((v, i) => (
              <option key={v.vehicle_id} value={i}>
                {v.label}
              </option>
            ))}
          </select>
        </div>
      )}
      <YearByCostChart
        years={years}
        totalFuel={totalFuel}
        totalDepreciation={totalDepreciation}
      />
    </div>
  );
}
